/**
 * @voltxampere/engine — Armijo backtracking line-search (Sprint 8c)
 *
 * Sprint 8b.1's diagnostic (see
 * `docs/sprints/diagnostics/sprint-8c-pnp-diagnostic.md` Test 2)
 * established that from a near-OP iterate, the PNP CE fixture's
 * reduced-KCL residual falls by up to 309× for an optimally-sized
 * Newton step — plain `α = 1` is NOT the minimiser. Sprint 8c's
 * primary rescue tool is therefore Armijo backtracking on the
 * reduced-KCL infinity norm: accept the largest `α ∈ (0, 1]`
 * satisfying a sufficient-decrease criterion.
 *
 * The formulation:
 *
 *   merit(x)     = ||f(x)||∞  projected onto `nodeIndexSet`
 *   descent check: for Newton direction `p = -J⁻¹ f`, the
 *                  directional derivative of `merit` at `x` in
 *                  direction `p` is ≤ -merit(x) (strict descent
 *                  for a non-singular Jacobian). Operationally we
 *                  treat it as `-merit(x)` in the Armijo bound.
 *   Armijo bound: merit(x + αp) ≤ (1 - c1·α) · merit(x)
 *   backtrack:    α ← ρ·α
 *
 * Defaults follow Nocedal & Wright *Numerical Optimization*
 * Algorithm 3.1: `c1 = 1e-4`, `ρ = 0.5`, `maxBacktracks = 20`.
 * After 20 halvings `α ≈ 1e-6`, below which further backtracking
 * is wasted work — the caller should fall through to the next
 * strategy in the DC ladder.
 *
 * Descent-direction guard: if `merit(x + p) > merit(x)` (full
 * Newton step already makes things worse), Armijo's bound with
 * any positive `α` would fail the sufficient-decrease condition.
 * The guard detects this and returns `accepted: false` at the
 * first trial so the caller can invoke its fallback (delta-V
 * damping from Sprint 8a) without paying for 20 residual
 * evaluations. The guard also catches the trivial-fixed-point
 * case: when `merit(x) = 0` the relative decrease test always
 * passes but the step itself may be zero-descent — we return
 * `accepted: true, alpha: 0, xNew = x` so the caller can surface
 * the DC warning.
 *
 * Cost accounting: each backtrack triggers one residual evaluation,
 * which requires a full MNA rebuild at the trial iterate. That's
 * expensive (comparable to a Newton iteration), so Armijo is
 * gated in the Newton driver: only active when initial conditions
 * are supplied OR stall detection has fired (see
 * `./stall-detection.ts`).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
import { normReducedInf } from "./residual.js";
/**
 * Default Armijo parameters per Nocedal & Wright §3.1.
 * Exposed as a public constant so tests and `/ultrareview`
 * diagnostics can refer to the same numbers as the driver.
 */
export const DEFAULT_ARMIJO_OPTIONS = {
    c1: 1e-4,
    rho: 0.5,
    maxBacktracks: 20,
};
/**
 * Run Armijo backtracking line-search on `merit(x) = ‖f(x)‖∞`
 * reduced to `nodeIndexSet`.
 *
 * @param x_k current iterate (length `plan.size`)
 * @param p_k Newton direction `xNewRaw - x_k` (same length)
 * @param f_k residual at `x_k` — caller MUST supply a freshly
 *            computed `Y·x_k - rhs`; the function trusts this
 *            value and does not recompute it to save a build.
 * @param evalResidual pure function `x → f(x)` — performs a
 *            fresh MNA build and subtraction. Caller's
 *            responsibility to sandbox any shared state (e.g.
 *            pnjlim history) so the line-search probes don't
 *            pollute the driver's continuity.
 * @param nodeIndexSet MNA indices carrying node-KCL equations
 *            (usually `plan.nodeIndexSet`).
 * @param options Armijo parameters. Defaults to `DEFAULT_ARMIJO_OPTIONS`.
 */
export function armijoLineSearch(x_k, p_k, f_k, evalResidual, nodeIndexSet, options = DEFAULT_ARMIJO_OPTIONS) {
    if (x_k.length !== p_k.length || x_k.length !== f_k.length) {
        throw new ContractViolationError(`armijoLineSearch: x_k/p_k/f_k length mismatch (x=${String(x_k.length)}, p=${String(p_k.length)}, f=${String(f_k.length)})`);
    }
    if (!(options.c1 > 0 && options.c1 < 1)) {
        throw new ContractViolationError(`armijoLineSearch: c1 must lie in (0, 1), got ${String(options.c1)}`);
    }
    if (!(options.rho > 0 && options.rho < 1)) {
        throw new ContractViolationError(`armijoLineSearch: rho must lie in (0, 1), got ${String(options.rho)}`);
    }
    if (!Number.isInteger(options.maxBacktracks) || options.maxBacktracks <= 0) {
        throw new ContractViolationError(`armijoLineSearch: maxBacktracks must be a positive integer, got ${String(options.maxBacktracks)}`);
    }
    const meritK = normReducedInf(f_k, nodeIndexSet);
    // Trivial fixed-point short-circuit: ‖f(x_k)‖ = 0 means x_k is
    // already a fixed point of the reduced-KCL equations. The caller
    // (Newton driver) will surface a warning if this is the BJT-off
    // trap rather than the physically interesting OP. Return
    // accepted=true with alpha=0 so the Newton loop's convergence
    // check sees no change and exits cleanly rather than infinite-
    // looping on a zero-norm fixed point.
    if (meritK === 0) {
        return {
            alpha: 0,
            xNew: x_k,
            fNew: f_k,
            accepted: true,
            evaluations: 0,
        };
    }
    // Newton direction's directional derivative of the merit
    // function is ≤ -‖f‖ (strict descent for a non-singular
    // Jacobian). For the Armijo bound we use -merit as a safe
    // lower bound: any α satisfying the bound also satisfies the
    // finer gradient-based bound Nocedal & Wright write with
    // `∇m(x)·p`. This is conservative — the actual step may
    // produce a larger decrease, which is fine for acceptance.
    const directionalDeriv = -meritK;
    let alpha = 1;
    let evaluations = 0;
    const n = x_k.length;
    const xTrial = new Float64Array(n);
    for (let backtrack = 0; backtrack < options.maxBacktracks; backtrack++) {
        for (let i = 0; i < n; i++) {
            xTrial[i] = (x_k[i] ?? 0) + alpha * (p_k[i] ?? 0);
        }
        const fTrial = evalResidual(xTrial);
        evaluations += 1;
        const meritTrial = normReducedInf(fTrial, nodeIndexSet);
        // Armijo sufficient-decrease: merit(x+αp) ≤ merit(x) + c1·α·(-merit(x))
        //                          = (1 - c1·α) · merit(x)
        const bound = meritK + options.c1 * alpha * directionalDeriv;
        if (meritTrial <= bound) {
            // `xTrial` is owned by this function across iters; return a
            // fresh copy so the caller can't mutate the line-search
            // scratch buffer through the returned reference.
            const xAccepted = new Float64Array(xTrial);
            return {
                alpha,
                xNew: xAccepted,
                fNew: fTrial,
                accepted: true,
                evaluations,
            };
        }
        alpha *= options.rho;
    }
    // All backtracks failed — return the original iterate so the
    // caller falls through to its next strategy without having to
    // undo a pending step.
    return {
        alpha: 0,
        xNew: x_k,
        fNew: f_k,
        accepted: false,
        evaluations,
    };
}
//# sourceMappingURL=line-search.js.map